import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { companyAPI } from '../services/api';
import BackButton from '../components/BackButton';
import { fmtDate } from '../utils/date';
import { PageError, loadError } from '../components/OfflineNotice';

const TITLES = {
  'revenue':      { label: 'Revenue',      subtitle: 'All sales recorded' },
  'gross-profit': { label: 'Gross Profit', subtitle: 'Sales revenue vs cost of goods' },
  'expenses':     { label: 'Expenses',     subtitle: 'Operating expenses' },
  'net-profit':   { label: 'Net Profit',   subtitle: 'Revenue minus all costs and expenses' },
  'receivables':  { label: 'Receivables',  subtitle: 'Outstanding payments owed' },
  'stock-value':  { label: 'Stock Value',  subtitle: 'Current inventory valuation' },
};

export default function BusinessKpiDetail() {
  const { id, type } = useParams();
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    companyAPI.getBusinessKpiDetail(id, type)
      .then(r => setData(r.data))
      .catch((err) => setError(loadError(err, 'Failed to load detail')))
      .finally(() => setLoading(false));
  }, [id, type]);

  const fmt = (n) =>
    new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

  const meta = TITLES[type] || { label: type, subtitle: '' };

  if (loading) return <div className="page"><BackButton /><p className="text-muted">Loading…</p></div>;
  if (error)   return <div className="page"><BackButton /><PageError error={error} /></div>;

  const cur = data.business.currency;

  const sectionHeader = (title, total, color) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem', marginTop: '1rem' }}>
      <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{title}</span>
      {total !== undefined && (
        <span style={{ fontWeight: 700, fontSize: '0.88rem', color: color || 'inherit' }}>{cur} {fmt(total)}</span>
      )}
    </div>
  );

  const SaleRow = ({ r }) => {
    const expired  = r.expiry_date && new Date(r.expiry_date) < new Date();
    const expiring = r.expiry_date && !expired && (new Date(r.expiry_date) - new Date()) < 30 * 24 * 60 * 60 * 1000;
    return (
      <div className="card" style={{ marginBottom: '0.45rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{r.product_name || '—'}</div>
            <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.1rem' }}>
              {r.buyer_name} · qty {Number(r.qty).toFixed(2)} @ {cur} {fmt(r.unit_price)} · {fmtDate(r.sold_at)}
            </div>
            {r.expiry_date && (
              <div style={{ fontSize: '0.75rem', marginTop: '0.1rem', color: expired ? 'var(--color-danger)' : expiring ? 'var(--color-warning)' : 'var(--color-muted)' }}>
                {expired ? 'Expired' : 'Expires'}: {fmtDate(r.expiry_date)}
              </div>
            )}
            {type === 'receivables' && Number(r.outstanding) > 0 && (
              <div style={{ fontSize: '0.75rem', color: 'var(--color-warning)', marginTop: '0.1rem' }}>
                Outstanding: {cur} {fmt(r.outstanding)}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.2rem', flexShrink: 0, marginLeft: '0.75rem' }}>
            <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>{cur} {fmt(r.total_amount)}</span>
            <span className={`badge badge-${r.payment_status}`} style={{ fontSize: '0.62rem' }}>{r.payment_status}</span>
          </div>
        </div>
      </div>
    );
  };

  const PurchaseRow = ({ r }) => (
    <div className="card" style={{ marginBottom: '0.45rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{r.product_name || '—'}</div>
          <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.1rem' }}>
            qty {Number(r.qty).toFixed(2)} @ {cur} {fmt(r.unit_cost)}{r.supplier ? ` · ${r.supplier}` : ''} · {fmtDate(r.purchased_at)}
          </div>
        </div>
        <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--color-danger)', flexShrink: 0, marginLeft: '0.75rem' }}>
          − {cur} {fmt(r.total_cost)}
        </span>
      </div>
    </div>
  );

  const ExpenseRow = ({ r }) => (
    <div className="card" style={{ marginBottom: '0.45rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', textTransform: 'capitalize' }}>{r.category}</div>
          {r.description && (
            <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.1rem' }}>{r.description} · {fmtDate(r.expense_at)}</div>
          )}
          {!r.description && (
            <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.1rem' }}>{fmtDate(r.expense_at)}</div>
          )}
        </div>
        <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--color-danger)', flexShrink: 0, marginLeft: '0.75rem' }}>
          − {cur} {fmt(r.amount)}
        </span>
      </div>
    </div>
  );

  // Revenue per product, biggest first — the "what was sold" summary.
  const byProduct = [];
  if (type === 'revenue') {
    const groups = new Map();
    for (const r of data.sales || []) {
      const key = r.product_id || 'none';
      const g = groups.get(key) || { key, product_id: r.product_id, name: r.product_name || '—', count: 0, qty: 0, total: 0 };
      g.count += 1;
      g.qty += Number(r.qty);
      g.total += Number(r.total_amount);
      groups.set(key, g);
    }
    byProduct.push(...[...groups.values()].sort((a, b) => b.total - a.total));
  }

  const totalSales     = (data.sales     || []).reduce((s, r) => s + Number(r.total_amount), 0);
  const totalPurchases = (data.purchases || []).reduce((s, r) => s + Number(r.total_cost), 0);
  const totalExpenses  = (data.expenses  || []).reduce((s, r) => s + Number(r.amount), 0);

  return (
    <div className="page">
      <BackButton />
      <h1 className="page-title">{meta.label}</h1>
      <p className="text-muted" style={{ fontSize: '0.82rem', marginBottom: '1.25rem' }}>
        {data.business.name} · {meta.subtitle}
      </p>

      {/* Revenue: what was sold, per product */}
      {type === 'revenue' && byProduct.length > 0 && (
        <>
          {sectionHeader('What was sold', totalSales)}
          {byProduct.map(g => (
            <div
              key={g.key}
              className="card"
              onClick={g.product_id ? () => navigate(`/company/business/${id}/product/${g.product_id}`) : undefined}
              style={{ marginBottom: '0.45rem', cursor: g.product_id ? 'pointer' : 'default' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{g.name}</div>
                  <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.1rem' }}>
                    {g.count} {g.count === 1 ? 'sale' : 'sales'} · qty {g.qty.toFixed(2)}
                  </div>
                </div>
                <span style={{ fontWeight: 700, fontSize: '0.88rem', flexShrink: 0, marginLeft: '0.75rem' }}>{cur} {fmt(g.total)}</span>
              </div>
            </div>
          ))}
        </>
      )}

      {/* Revenue / Receivables / Gross-profit sales section */}
      {data.sales && (
        <>
          {(type === 'gross-profit' || type === 'net-profit') && sectionHeader('Sales revenue', totalSales, 'var(--color-success)')}
          {type === 'receivables' && sectionHeader(`${data.sales.length} outstanding ${data.sales.length === 1 ? 'sale' : 'sales'}`)}
          {type === 'revenue'     && sectionHeader(`${data.sales.length} ${data.sales.length === 1 ? 'sale' : 'sales'}`, totalSales)}
          {data.sales.length === 0
            ? <p className="text-muted" style={{ fontSize: '0.88rem' }}>No sales recorded.</p>
            : data.sales.map(r => <SaleRow key={r.id} r={r} />)
          }
        </>
      )}

      {/* Purchases section */}
      {data.purchases && (
        <>
          {sectionHeader('Cost of goods (purchases)', totalPurchases, 'var(--color-danger)')}
          {data.purchases.length === 0
            ? <p className="text-muted" style={{ fontSize: '0.88rem' }}>No purchases recorded.</p>
            : data.purchases.map(r => <PurchaseRow key={r.id} r={r} />)
          }
        </>
      )}

      {/* Expenses section */}
      {data.expenses && (
        <>
          {(type === 'net-profit') && sectionHeader('Operating expenses', totalExpenses, 'var(--color-danger)')}
          {type === 'expenses'     && sectionHeader(`${data.expenses.length} ${data.expenses.length === 1 ? 'expense' : 'expenses'}`, totalExpenses, 'var(--color-danger)')}
          {data.expenses.length === 0
            ? <p className="text-muted" style={{ fontSize: '0.88rem' }}>No expenses recorded.</p>
            : data.expenses.map(r => <ExpenseRow key={r.id} r={r} />)
          }
        </>
      )}

      {/* Net profit summary line */}
      {type === 'net-profit' && (
        <div className="card" style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Net Profit</span>
          <span style={{
            fontWeight: 700, fontSize: '1rem',
            color: (totalSales - totalPurchases - totalExpenses) >= 0 ? 'var(--color-success)' : 'var(--color-danger)',
          }}>
            {cur} {fmt(totalSales - totalPurchases - totalExpenses)}
          </span>
        </div>
      )}

      {/* Gross profit summary line */}
      {type === 'gross-profit' && (
        <div className="card" style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Gross Profit</span>
          <span style={{
            fontWeight: 700, fontSize: '1rem',
            color: (totalSales - totalPurchases) >= 0 ? 'var(--color-success)' : 'var(--color-danger)',
          }}>
            {cur} {fmt(totalSales - totalPurchases)}
          </span>
        </div>
      )}

      {/* Stock value */}
      {data.products && (
        <>
          {sectionHeader(`${data.products.length} products in stock`)}
          {data.products.length === 0
            ? <p className="text-muted" style={{ fontSize: '0.88rem' }}>No stock on hand.</p>
            : data.products.map(p => (
              <div key={p.id} className="card" style={{ marginBottom: '0.45rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{p.name}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.1rem' }}>
                      qty {Number(p.stock_qty).toFixed(2)} × {cur} {fmt(p.unit_cost)} avg cost
                      {p.sku ? ` · SKU ${p.sku}` : ''}
                    </div>
                  </div>
                  <span style={{ fontWeight: 700, fontSize: '0.88rem', flexShrink: 0, marginLeft: '0.75rem' }}>
                    {cur} {fmt(p.stock_value)}
                  </span>
                </div>
              </div>
            ))
          }
          <div className="card" style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Total stock value</span>
            <span style={{ fontWeight: 700, fontSize: '1rem' }}>
              {cur} {fmt(data.products.reduce((s, p) => s + Number(p.stock_value), 0))}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
